import { TFunction } from "@einride/saga-core"
import {
  AggregationInterval,
  Comparison,
  DateSpanFragment,
  DateSpanInterval,
  MetricDateSpan,
  TimeComparisonInterval,
} from "gen/graphql"
import { toDateTime } from "lib/date"
import { CustomTimespan, isCustomTimespan, TimespanKey } from "lib/params"
import { DateTime } from "luxon"

export type DateOption = {
  key: DateSpanInterval
  label: {
    left: string
    right: string
  }
}

export type Option = {
  key: TimeComparisonInterval
  label: string
  value: string
}

export type DateWithComparison = {
  options: DateOption[]
  comparision: Option[]
}

export const DATE_VALUE_FORMAT = "yyyy-LL-dd"
export const DATE_LABEL_FORMAT = "LLL dd, yyyy"

export function parseTimespan(timespan: CustomTimespan): [DateTime, DateTime] {
  const parts = timespan.split(",")
  const startTime = DateTime.fromISO(parts[0], { zone: "utc" })
  const endTime = DateTime.fromISO(parts[1], { zone: "utc" })

  return [startTime, endTime]
}

export function formatComparisonLabel(
  t: TFunction<"explore">,
  key: string | undefined,
  fallbackLabel: string,
): string {
  if (!key) {
    return fallbackLabel
  }
  const period = key.split("-")
  if (period.length === 0) {
    return fallbackLabel
  }
  switch (period[1]) {
    case "previousPeriod":
      return t("common.previousPeriod")
    case "lastYear":
      return t("common.lastYear")
    default:
      return fallbackLabel
  }
}

export function getComparisonDateSpan(
  key: TimespanKey | undefined,
  comparision: Comparison | undefined | null,
  interval: AggregationInterval,
  allTimeStart?: DateTime,
  custom?: [DateSpanFragment["startDate"], DateSpanFragment["endDate"]],
): CustomTimespan | undefined {
  const timespan = getDateSpan(key, DateTime.now(), interval, allTimeStart, custom)
  const [startDate, endDate] = parseTimespan(timespan)
  if (comparision?.interval) {
    if (comparision.interval === TimeComparisonInterval.LastYear) {
      const lastYearStartDate = startDate.minus({ year: 1 })
      const lastYearEndDate = endDate.minus({ year: 1 })
      return `${lastYearStartDate.toFormat(DATE_VALUE_FORMAT)},${lastYearEndDate.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
    if (comparision.interval === TimeComparisonInterval.PreviousPeriod) {
      const duration = endDate.diff(startDate)
      const previousStartDate = startDate.minus(duration)
      return `${previousStartDate.toFormat(DATE_VALUE_FORMAT)},${startDate.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
  } else if (comparision?.metric) {
    return getDateSpan(key, DateTime.now(), interval, allTimeStart, custom)
  }
  return undefined
}

// getDateSpan returns a time span based on the provided key. returned start time is inclusive and end time is exclusive.
export function getDateSpan(
  key: TimespanKey | undefined,
  now: DateTime,
  interval?: AggregationInterval,
  allTimeStart?: DateTime,
  custom?: [DateSpanFragment["startDate"], DateSpanFragment["endDate"]],
): CustomTimespan {
  if (isCustomTimespan(key)) {
    return key
  }
  switch (key) {
    case DateSpanInterval.AllTime: {
      return `${allTimeStart?.toFormat(DATE_VALUE_FORMAT)},${now
        .plus({ day: 1 })
        .toFormat(DATE_VALUE_FORMAT)}`
    }

    case DateSpanInterval.LastMonth: {
      const startOfThisMonth = now.startOf("month")
      let startOfLastMonth = startOfThisMonth.plus({ months: -1 })
      if (interval === AggregationInterval.Week) {
        startOfLastMonth = startOfLastMonth.startOf("week")
      }
      return `${startOfLastMonth.toFormat(DATE_VALUE_FORMAT)},${startOfThisMonth.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
    case DateSpanInterval.LastQuarter: {
      const startOfThisQuarter = now.startOf("quarter")
      let startOfLastQuarter = startOfThisQuarter.plus({ months: -3 })
      if (interval === AggregationInterval.Week) {
        startOfLastQuarter = startOfLastQuarter.startOf("week")
      }
      return `${startOfLastQuarter.toFormat(DATE_VALUE_FORMAT)},${startOfThisQuarter.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }

    case DateSpanInterval.LastWeek: {
      const startOfThisWeek = now.startOf("week")
      const startOfLastWeek = startOfThisWeek.plus({ days: -7 })
      return `${startOfLastWeek.toFormat(DATE_VALUE_FORMAT)},${startOfThisWeek.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
    case DateSpanInterval.LastYear: {
      const startOfThisYear = now.startOf("year")
      let startOfLastYear = startOfThisYear.plus({ years: -1 })
      if (interval === AggregationInterval.Week) {
        startOfLastYear = startOfLastYear.startOf("week")
      }
      return `${startOfLastYear.toFormat(DATE_VALUE_FORMAT)},${startOfThisYear.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
    case DateSpanInterval.ThisMonth: {
      let startOfThisMonth = now.startOf("month")
      if (interval === AggregationInterval.Week) {
        startOfThisMonth = startOfThisMonth.startOf("week")
      }
      return `${startOfThisMonth.toFormat(DATE_VALUE_FORMAT)},${now
        .plus({ day: 1 })
        .toFormat(DATE_VALUE_FORMAT)}`
    }
    case DateSpanInterval.ThisQuarter: {
      let startOfThisQuarter = now.startOf("quarter")
      if (interval === AggregationInterval.Week) {
        startOfThisQuarter = startOfThisQuarter.startOf("week")
      }
      return `${startOfThisQuarter.toFormat(DATE_VALUE_FORMAT)},${now
        .plus({ day: 1 })
        .toFormat(DATE_VALUE_FORMAT)}`
    }
    case DateSpanInterval.ThisWeek: {
      const startOfThisWeek = now.startOf("week")
      const endOfThisWeek = now.endOf("week")
      return `${startOfThisWeek.toFormat(DATE_VALUE_FORMAT)},${endOfThisWeek
        .plus({ day: 1 })
        .toFormat(DATE_VALUE_FORMAT)}`
    }
    case DateSpanInterval.ThreeYearsAgo: {
      let startOfThreeYearsAgo = now.startOf("year").minus({ year: 3 })
      const endOfThreeYearsAgo = startOfThreeYearsAgo.endOf("year").plus({ day: 1 })
      if (interval === AggregationInterval.Week) {
        startOfThreeYearsAgo = startOfThreeYearsAgo.startOf("week")
      }
      return `${startOfThreeYearsAgo.toFormat(DATE_VALUE_FORMAT)},${endOfThreeYearsAgo.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
    case DateSpanInterval.TwoYearsAgo: {
      let startOfTwoYearsAgo = now.startOf("year").minus({ year: 2 })
      const endOfTwoYearsAgo = startOfTwoYearsAgo.endOf("year").plus({ day: 1 })
      if (interval === AggregationInterval.Week) {
        startOfTwoYearsAgo = startOfTwoYearsAgo.startOf("week")
      }
      return `${startOfTwoYearsAgo.toFormat(DATE_VALUE_FORMAT)},${endOfTwoYearsAgo.toFormat(
        DATE_VALUE_FORMAT,
      )}`
    }
    case DateSpanInterval.YearToDate: {
      let startOfYear = now.startOf("year")
      if (interval === AggregationInterval.Week) {
        startOfYear = startOfYear.startOf("week")
      }
      return `${startOfYear.toFormat(DATE_VALUE_FORMAT)},${now
        .plus({ day: 1 })
        .toFormat(DATE_VALUE_FORMAT)}`
    }
    case DateSpanInterval.Custom: {
      if (custom && custom[0] && custom[1]) {
        const start = toDateTime(custom[0]?.year, custom[0]?.month, custom[0]?.day)
        const end = toDateTime(custom[1]?.year, custom[1]?.month, custom[1]?.day)
        return `${start.toFormat(DATE_VALUE_FORMAT)},${end.toFormat(DATE_VALUE_FORMAT)}`
      }
      return ","
    }
    default: {
      // default to year to date
      const startOfYear = now.startOf("year")
      return `${startOfYear.toFormat(DATE_VALUE_FORMAT)},${now.toFormat(DATE_VALUE_FORMAT)}`
    }
  }
}

function formatLabel(
  t: TFunction<"explore">,
  key: DateSpanInterval,
  label: string,
  startTime: DateTime,
  intervalType: "years" | "months" | "days",
  intervalValue: number,
): DateOption {
  const endTime = startTime.plus({
    [intervalType]: intervalValue,
    day: intervalType === "days" ? intervalValue - 1 : -1,
  })
  const now = DateTime.now()
  const anchor = endTime > now ? now : endTime
  const labelRightValue = endTime > now ? t("common.now") : anchor.toFormat(DATE_LABEL_FORMAT)

  return {
    key,
    label: {
      left: label,
      right: `${startTime.toFormat(DATE_LABEL_FORMAT)} – ${labelRightValue}`,
    },
  }
}

export function getYearlyOptions(
  t: TFunction<"explore">,
  startDate?: { startYear: number; startMonth: number },
): DateWithComparison {
  const now = new Date()
  const startOfThisYear = DateTime.fromJSDate(new Date(now.getFullYear(), 0, 1))
  const startOfLastYear = startOfThisYear.plus({ years: -1 })
  const twoYearsAgo = startOfThisYear.plus({ years: -2 })
  const threeYearsAgo = startOfThisYear.plus({ years: -3 })
  const lastYearOption = formatLabel(
    t,
    DateSpanInterval.LastYear,
    t("common.lastYear"),
    startOfLastYear,
    "years",
    1,
  )

  const config: DateWithComparison = {
    options: [
      formatLabel(
        t,
        DateSpanInterval.YearToDate,
        t("common.thisYear"),
        startOfThisYear,
        "years",
        1,
      ),
      lastYearOption,
      formatLabel(
        t,
        DateSpanInterval.TwoYearsAgo,
        twoYearsAgo.toFormat("yyyy"),
        twoYearsAgo,
        "years",
        1,
      ),
      formatLabel(
        t,
        DateSpanInterval.ThreeYearsAgo,
        threeYearsAgo.toFormat("yyyy"),
        threeYearsAgo,
        "years",
        1,
      ),
    ],
    comparision: [
      {
        key: TimeComparisonInterval.PreviousPeriod,
        label: t("common.previousPeriod"),
        value: "year-lastYear",
      },
    ],
  }

  if (startDate) {
    config.options.push(
      formatLabel(
        t,
        DateSpanInterval.AllTime,
        t("common.allTime"),
        DateTime.fromJSDate(new Date(startDate.startYear, startDate.startMonth - 1, 1)),
        "years",
        // make sure it is next year, then formatLabel adjusts to today
        now.getFullYear() - startDate.startYear + 1,
      ),
    )
  }

  return config
}

export function getQuarterlyOptions(t: TFunction<"explore">): DateWithComparison {
  const now = new Date()
  const startOfThisQuarter = DateTime.fromJSDate(
    new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1),
  )
  const startOfLastQuarter = startOfThisQuarter.plus({ months: -3 })
  const lastQuarterOption = formatLabel(
    t,
    DateSpanInterval.LastQuarter,
    t("common.lastQuarter"),
    startOfLastQuarter,
    "months",
    3,
  )

  return {
    options: [
      formatLabel(
        t,
        DateSpanInterval.ThisQuarter,
        t("common.thisQuarter"),
        startOfThisQuarter,
        "months",
        3,
      ),
      lastQuarterOption,
    ],
    comparision: [
      {
        key: TimeComparisonInterval.PreviousPeriod,
        label: t("common.previousPeriod"),
        value: "quarter-previousPeriod",
      },
      {
        key: TimeComparisonInterval.LastYear,
        label: t("common.lastYear"),
        value: "quarter-lastYear",
      },
    ],
  }
}

export function getMonthlyOptions(t: TFunction<"explore">): DateWithComparison {
  const now = new Date()
  const startOfThisMonth = DateTime.fromJSDate(new Date(now.getFullYear(), now.getMonth(), 1))
  const startOfLastMonth = startOfThisMonth.plus({ months: -1 })
  const lastMonthOption = formatLabel(
    t,
    DateSpanInterval.LastMonth,
    t("common.lastMonth"),
    startOfLastMonth,
    "months",
    1,
  )

  return {
    options: [
      formatLabel(
        t,
        DateSpanInterval.ThisMonth,
        t("common.thisMonth"),
        startOfThisMonth,
        "months",
        1,
      ),
      lastMonthOption,
    ],
    comparision: [
      {
        key: TimeComparisonInterval.PreviousPeriod,
        label: t("common.previousPeriod"),
        value: "month-previousPeriod",
      },
      {
        key: TimeComparisonInterval.LastYear,
        label: t("common.lastYear"),
        value: "month-lastYear",
      },
    ],
  }
}

export function getWeeklyOptions(t: TFunction<"explore">): DateWithComparison {
  const now = new Date()
  const startOfThisWeek = DateTime.fromJSDate(new Date(now.setDate(now.getDate() - now.getDay())))
  const startOfLastWeek = startOfThisWeek.plus({ days: -7 })

  return {
    options: [
      formatLabel(t, DateSpanInterval.ThisWeek, t("common.thisWeek"), startOfThisWeek, "days", 7),
      formatLabel(t, DateSpanInterval.LastWeek, t("common.lastWeek"), startOfLastWeek, "days", 7),
    ],
    comparision: [
      {
        key: TimeComparisonInterval.PreviousPeriod,
        label: t("common.previousPeriod"),
        value: "week-previousPeriod",
      },
      { key: TimeComparisonInterval.LastYear, label: t("common.lastYear"), value: "week-lastYear" },
    ],
  }
}

export function getDefaultOption(t: TFunction<"explore">): DateSpanInterval {
  return getYearlyOptions(t).options[0].key
}

export function getComparisonDateOption(
  comparisonKey: TimeComparisonInterval,
  t: TFunction<"explore">,
  startDate?: { startYear: number; startMonth: number },
): Option {
  const yearlyOptions = getYearlyOptions(t, startDate)
  const quarterlyOptions = getQuarterlyOptions(t)
  const monthlyOptions = getMonthlyOptions(t)
  const weeklyOptions = getWeeklyOptions(t)

  const options = [
    ...yearlyOptions.comparision,
    ...quarterlyOptions.comparision,
    ...monthlyOptions.comparision,
    ...weeklyOptions.comparision,
  ]
  const selected = options.find((option) => option.key === comparisonKey)
  return selected ?? options[0]
}

export function getDateOption(
  timespanKey: TimespanKey | undefined,
  t: TFunction<"explore">,
  startDate?: { startYear: number; startMonth: number },
): DateOption {
  if (isCustomTimespan(timespanKey) || timespanKey === DateSpanInterval.Custom) {
    return {
      key: DateSpanInterval.Custom,
      label: {
        left: t("common.custom"),
        right: timespanKey,
      },
    }
  }
  const yearlyOptions = getYearlyOptions(t, startDate)
  const quarterlyOptions = getQuarterlyOptions(t)
  const monthlyOptions = getMonthlyOptions(t)
  const weeklyOptions = getWeeklyOptions(t)

  const options = [
    ...yearlyOptions.options,
    ...quarterlyOptions.options,
    ...monthlyOptions.options,
    ...weeklyOptions.options,
  ]
  const selected = options.find((option) => option.key === timespanKey)
  return selected ?? options[0]
}

export function formatTimespan(
  t: TFunction<"explore">,
  datespan?: MetricDateSpan,
  startDate?: { startYear: number; startMonth: number },
  timespan?: TimespanKey,
): string {
  if (isCustomTimespan(timespan)) {
    return timespan
      .split(",")
      .map((date) => DateTime.fromISO(date, { zone: "utc" }).toFormat(DATE_LABEL_FORMAT))
      .join(" – ")
  }
  if (datespan?.interval === DateSpanInterval.Custom && datespan.startDate && datespan.endDate) {
    return [datespan.startDate, datespan.endDate]
      .map((date) =>
        DateTime.fromObject({ day: date.day, month: date.month, year: date.year }).toFormat(
          DATE_LABEL_FORMAT,
        ),
      )
      .join(" – ")
  }

  const timespanInterval = timespan ?? datespan?.interval
  return getDateOption(timespanInterval, t, startDate).label.left
}
